import React, { Component } from "react";

import styled from "styled-components/macro";

import {
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@material-ui/core";

import { spacing } from "@material-ui/system";

import { getReferralAllStatus } from "../services/referallService";
import { withTranslation } from "react-i18next";
import {
  formatCurrency,
  formatDate,
  formatNumber,
} from "../utils/localization";

const Paper = styled(MuiPaper)(spacing);

const TableShell = styled(Paper)`
  border-radius: 0 0 26px 26px;
  box-shadow: none;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
`;

const HeaderCell = styled(TableCell)`
  && {
    background: #f8fbff;
    color: #475569;
    font-size: 0.76rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    border-bottom: 1px solid rgba(226, 232, 240, 0.9);
    white-space: nowrap;
  }
`;

const BodyCell = styled(TableCell)`
  && {
    padding-top: 18px;
    padding-bottom: 18px;
    border-bottom: 1px solid rgba(241, 245, 249, 1);
    color: #0f172a;
  }
`;

const NameCell = styled(BodyCell)`
  && {
    font-weight: 700;
  }
`;

const DataRow = styled(TableRow)`
  && {
    transition: transform 0.18s ease, box-shadow 0.18s ease,
      background 0.18s ease;
  }

  &:hover {
    background: rgba(239, 246, 255, 0.7);
    transform: translateY(-1px);
    box-shadow: inset 0 0 0 1px rgba(186, 230, 253, 0.7);
  }
`;

const ValuePill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: ${(props) => props.bg || "#f8fafc"};
  color: ${(props) => props.color || "#0f172a"};
  font-weight: 700;
  line-height: 1;
`;

const SecondaryText = styled.span`
  color: #64748b;
  font-weight: 600;
`;

const headCells = [
  { id: "name", alignment: "left", translationKey: "referrals.client" },
  {
    id: "creationDate",
    alignment: "left",
    translationKey: "referrals.registrationDate",
  },
  {
    id: "balance",
    alignment: "left",
    translationKey: "referrals.currentBalance",
  },
  {
    id: "budget",
    alignment: "left",
    translationKey: "referrals.campaignBudget",
  },
  { id: "activeAds", alignment: "left", translationKey: "referrals.activeAds" },
  {
    id: "lastPrePayTransaction",
    alignment: "left",
    translationKey: "referrals.lastTopUp",
  },
];

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, t } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <HeaderCell
            key={headCell.id}
            align={headCell.alignment}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {t(headCell.translationKey)}
            </TableSortLabel>
          </HeaderCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

class ReferralTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rows: [],
      order: "asc",
      orderBy: "customer",
      selected: [],
      page: 0,
      rowsPerPage: 10,
      props: props,
    };
  }

  async getOptions() {
    const res = await getReferralAllStatus();
    this.setState({ rows: res });
    if (this.props.onRowsLoaded) {
      this.props.onRowsLoaded(res);
    }
  }

  componentDidMount() {
    this.getOptions();
  }

  render() {
    const { t } = this.props;
    const handleRequestSort = (event, property) => {
      const isAsc =
        this.state.orderBy === property && this.state.order === "asc";
      this.setState({ order: isAsc ? "desc" : "asc", orderBy: property });
    };

    const handleChangePage = (event, newPage) => {
      this.setState({ page: newPage });
    };

    const handleChangeRowsPerPage = (event) => {
      this.setState({ rowsPerPage: parseInt(event.target.value, 10), page: 0 });
    };

    const isSelected = (id) => this.state.selected.indexOf(id) !== -1;

    const emptyRows =
      this.state.rowsPerPage -
      Math.min(
        this.state.rowsPerPage,
        this.state.rows.length - this.state.page * this.state.rowsPerPage
      );

    return (
      <div>
        <TableShell>
          <TableContainer>
            <Table
              aria-labelledby="tableTitle"
              size="medium"
              aria-label={t("referrals.title")}
            >
              <EnhancedTableHead
                order={this.state.order}
                orderBy={this.state.orderBy}
                onRequestSort={handleRequestSort}
                t={t}
              />
              <TableBody>
                {stableSort(
                  this.state.rows,
                  getComparator(this.state.order, this.state.orderBy)
                )
                  .slice(
                    this.state.page * this.state.rowsPerPage,
                    this.state.page * this.state.rowsPerPage +
                      this.state.rowsPerPage
                  )
                  .map((row, index) => {
                    const isItemSelected = isSelected(row.id);

                    return (
                      <DataRow
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={`${row.id}-${index}`}
                        selected={isItemSelected}
                      >
                        <NameCell align="left">{row.name}</NameCell>
                        <BodyCell align="left">
                          <SecondaryText>
                            {formatDate(row.creationDate)}
                          </SecondaryText>
                        </BodyCell>
                        <BodyCell align="left">
                          <ValuePill bg="#eff6ff" color="#0f4c81">
                            {formatCurrency(row.balance)}
                          </ValuePill>
                        </BodyCell>
                        <BodyCell align="left">
                          <ValuePill bg="#f8fafc" color="#334155">
                            {formatCurrency(row.adBudget)}
                          </ValuePill>
                        </BodyCell>
                        <BodyCell align="left">
                          <ValuePill bg="#ecfdf5" color="#166534">
                            {formatNumber(row.activeAds)}
                          </ValuePill>
                        </BodyCell>
                        <BodyCell align="left">
                          {row.lastPrePayTransaction == null ? (
                            <SecondaryText>
                              {t("common.notAvailable")}
                            </SecondaryText>
                          ) : (
                            row.lastPrePayTransaction
                          )}
                        </BodyCell>
                      </DataRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={8} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={this.state.rows.length}
            rowsPerPage={this.state.rowsPerPage}
            page={this.state.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableShell>
      </div>
    );
  }
}

export default withTranslation()(ReferralTable);
